import React, { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, ChevronLeft, ChevronRight,
  DoorClosed, Ruler, Building2, Layers, Calendar, Paintbrush, Compass, Blocks,
  Flame, Leaf, ArrowUpNarrowWide, Droplet, Plug, Fence, Wifi, UtensilsCrossed,
  Sun, Snowflake, Thermometer, Wind, Shield, Bell, Lock, Camera, Home,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { getApartmentSpec } from "../data/apartments";

const ICONS = {
  rooms: Home, area: Ruler, floor: Building2, totalFloors: Layers, yearBuilt: Calendar,
  finishing: Paintbrush, orientation: Compass, buildingType: Blocks, heating: Flame, energyClass: Leaf,
  terraceArea: Sun, loggiaArea: Sun, totalArea: Ruler, apartmentArea: Home,
  elevator: Building2, separateEntrance: DoorClosed, highCeilings: ArrowUpNarrowWide, newSewage: Droplet,
  newWiring: Plug, enclosedYard: Fence, internet: Wifi, kitchenLiving: UtensilsCrossed,
  terrace: Sun, loggia: Sun,
  airConditioning: Snowflake, underfloorHeating: Thermometer, plasticPipes: Droplet, heatRecovery: Wind,
  armoredDoor: Shield, alarm: Bell, codedLock: Lock, cctv: Camera,
};

/* -------- Photo gallery + lightbox -------- */
export const ApartmentGallery = ({ photos = [], title }) => {
  const [index, setIndex] = useState(null);
  const open = index !== null;

  const prev = useCallback((e) => { e && e.stopPropagation(); setIndex((i) => (i - 1 + photos.length) % photos.length); }, [photos.length]);
  const next = useCallback((e) => { e && e.stopPropagation(); setIndex((i) => (i + 1) % photos.length); }, [photos.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  if (!photos.length) return null;

  return (
    <div data-testid="apartment-gallery">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {photos.map((src, i) => (
          <button
            key={i}
            data-testid={`gallery-thumb-${i}`}
            onClick={() => setIndex(i)}
            className="bg-charcoal-light border border-cream/10 rounded-lg overflow-hidden flex items-center justify-center aspect-[4/3] hover:border-gold/40 transition-colors"
          >
            <img src={src} alt={`${title || ""} ${i + 1}`} className="w-full h-full object-contain" loading="lazy" />
          </button>
        ))}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-charcoal/95 flex items-center justify-center"
            data-testid="gallery-lightbox"
            onClick={() => setIndex(null)}
          >
            <button data-testid="lightbox-close" onClick={() => setIndex(null)} className="absolute top-5 right-5 text-cream/70 hover:text-gold z-10"><X size={30} /></button>
            {photos.length > 1 && (
              <>
                <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 sm:left-6 text-cream/70 hover:text-gold z-10 p-2"><ChevronLeft size={40} /></button>
                <button data-testid="lightbox-next" onClick={next} className="absolute right-3 sm:right-6 text-cream/70 hover:text-gold z-10 p-2"><ChevronRight size={40} /></button>
              </>
            )}
            <motion.img
              key={index}
              initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
              src={photos[index]}
              alt=""
              className="max-h-[90vh] max-w-[92vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="absolute bottom-5 text-cream/50 text-sm">{index + 1} / {photos.length}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* -------- "More about the apartment" spec -------- */
const Row = ({ iconKey, label, value }) => {
  const Icon = ICONS[iconKey] || Home;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-cream/5">
      <Icon size={19} strokeWidth={1.4} className="text-cream/40 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-cream/40">{label}</p>
        <p className="text-sm text-cream/90">{value}</p>
      </div>
    </div>
  );
};

const Chip = ({ iconKey, label }) => {
  const Icon = ICONS[iconKey] || Home;
  return (
    <span className="inline-flex items-center gap-2 border border-cream/15 rounded-full px-3.5 py-1.5 text-sm text-cream/80">
      <Icon size={16} strokeWidth={1.4} className="text-gold/70" />
      {label}
    </span>
  );
};

const GroupHeader = ({ children }) => (
  <h3 className="text-[11px] uppercase tracking-[0.25em] text-gold/70 mb-4 mt-2">{children}</h3>
);

export const ApartmentAbout = ({ apt }) => {
  const { t } = useLang();
  const spec = getApartmentSpec(apt);
  const s = (k) => t(`spec.${k}`);

  const orientation = (spec.orientation || []).map((d) => s(`dir_${d}`)).join(", ") || "—";

  const general = [
    { k: "rooms", v: spec.rooms },
    { k: spec.apartmentArea != null ? "totalArea" : "area", v: `${spec.area} m²` },
    ...(spec.apartmentArea != null ? [{ k: "apartmentArea", v: `${spec.apartmentArea} m²` }] : []),
    ...(spec.terraceArea
      ? [{ k: spec.additionalSpaces[0] === "loggia" ? "loggiaArea" : "terraceArea", v: `${spec.terraceArea} m²` }]
      : []),
    { k: "floor", v: spec.floor },
    { k: "totalFloors", v: spec.totalFloors },
    { k: "yearBuilt", v: spec.yearBuilt },
    { k: "finishing", v: s(`val_${spec.finishing}`) },
    { k: "orientation", v: orientation },
    { k: "buildingType", v: s(`val_${spec.buildingType}`) },
    { k: "heating", v: s(`val_${spec.heating}`) },
    { k: "energyClass", v: spec.energyClass },
  ];

  return (
    <div data-testid="apartment-about" className="mt-16">
      <h2 className="font-serif text-3xl sm:text-4xl font-light text-cream mb-10">{s("moreTitle")}</h2>

      <GroupHeader>{s("generalInfo")}</GroupHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 mb-10">
        {general.map((g) => (
          <Row key={g.k} iconKey={g.k} label={s(g.k)} value={g.v} />
        ))}
      </div>

      <GroupHeader>{s("features")}</GroupHeader>
      <div className="flex flex-wrap gap-2.5 mb-10">
        {spec.features.map((f) => <Chip key={f} iconKey={f} label={s(f)} />)}
      </div>

      <GroupHeader>{s("additionalSpaces")}</GroupHeader>
      <div className="flex flex-wrap gap-2.5 mb-10">
        {spec.additionalSpaces.map((f) => <Chip key={f} iconKey={f} label={s(f)} />)}
      </div>

      <GroupHeader>{s("additionalEquipment")}</GroupHeader>
      <div className="flex flex-wrap gap-2.5 mb-10">
        {spec.equipment.map((f) => <Chip key={f} iconKey={f} label={s(f)} />)}
      </div>

      <GroupHeader>{s("security")}</GroupHeader>
      <div className="flex flex-wrap gap-2.5">
        {spec.security.map((f) => <Chip key={f} iconKey={f} label={s(f)} />)}
      </div>
    </div>
  );
};
